import React, { useEffect, useState, useRef, useMemo } from "react"
import { Typography, Card } from "@mui/material"

import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs from "dayjs"
import LoadingScreen from "../../../LoadingScreen"

import { BarChart } from "@mui/x-charts/BarChart"
import { PickersActionBar } from "@mui/x-date-pickers/PickersActionBar"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
// import { formatCurrency } from "../../util/formatCurrency"
import { motion } from "framer-motion"
import DoubleArrowIcon from "@mui/icons-material/KeyboardDoubleArrowDown"

import { useDemo } from "../../../context/DemoContext"
import DemoCTA from "../../../components/DemoCTA"
import useMediaQuery from "@mui/material/useMediaQuery"

const currentDayjs = dayjs()

const defaultRowHeight = 40
const tableExtras = 130 // padding above the table

function AppealsInfoGroupBy() {
  const { hasDemoAccess, demoData } = useDemo()
  const [showDemoCTA, setShowDemoCTA] = useState(false)

  const [info, setInfo] = React.useState(null)
  const [selectedMonth, setSelectedMonth] = useState(null)
  const [selectedYear, setSelectedYear] = useState(currentDayjs)
  const [availableMonths, setAvailableMonths] = useState([])
  const [isExpanded, setIsExpanded] = useState(false)
  const [rowHeight, setRowHeight] = useState(defaultRowHeight)

  const [view, setView] = useState("by_escalated_amount")
  const isMobile = useMediaQuery("(max-width:450px)")

  const defaultHeight = isMobile ? 450 : 331

  const [cardHeight, setCardHeight] = useState(defaultHeight) // default height

  const cardRef = useRef(null)

  useEffect(() => {
    // ------------------------
    // DEMO MODE
    // ------------------------
    if (hasDemoAccess && demoData) {
      setAvailableMonths(demoData.availableMonths || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])

      const month = selectedMonth?.month() + 1
      const year = selectedYear?.year()

      let data = demoData.groupByStore

      if (!data) return

      let totalEscalations = 0
      const rows = data.map((r) => {
        totalEscalations += Number(r["Total Escalations"]) || 0
        return {
          ...r,
          store_label: r.store_label || r.store_name,
          "Escalated Commission": Number(r["Escalated Commission"]) || 0,
          "Escalated Rebates": Number(r["Escalated Rebates"]) || 0,
          "Resolved Commission": Number(r["Resolved Commission"]) || 0,
          "Resolved Rebates": Number(r["Resolved Rebates"]) || 0,
          "Total Escalations": Number(r["Total Escalations"]) || 0,
          "Escalated Commission Count": Number(r["Escalated Commission Count"]) || 0,
          "Escalated Rebates Count": Number(r["Escalated Rebates Count"]) || 0,
          "Resolved Commission Count": Number(r["Resolved Commission Count"]) || 0,
          "Resolved Rebates Count": Number(r["Resolved Rebates Count"]) || 0,
        }
      })

      setInfo({
        organizationName: data.organizationName || "Demo Organization",
        rows,
        totalEscalations,
      })

      return
    }
  }, [hasDemoAccess, demoData, selectedYear, selectedMonth])

  const handleYearChange = (newYear) => {
    setShowDemoCTA(true)
  }

  const chartSetting = useMemo(() => {
    if (info && info.rows.length > 0) {
      const minRowHeight = 30
      const calcRowHeight = 330 / info.rows.length
      const maxRowHeight = defaultHeight

      const rowH = Math.min(maxRowHeight, Math.max(minRowHeight, calcRowHeight))
      setRowHeight(rowH)
      return {
        height: info?.rows?.length * rowH,
        minHeight: "400px",
      }
    }
    return {}
  }, [info])

  const handleViewChange = (event, _view) => {
    setView(_view)
  }

  const seriesByEscalatedCount = [
    {
      dataKey: "Escalated Commission Count",
      label: "Escalated Commission Count",
      stack: "count",
      color: "#075ac6",
    },

    {
      dataKey: "Escalated Rebates Count",
      label: "Escalated Rebates Count",
      stack: "count",
    },

    {
      dataKey: "Resolved Commission Count",
      label: "Recovered Commission Count",
      stack: "values",
      color: "rgb(48 255 0)",
    },
    {
      dataKey: "Resolved Rebates Count",
      label: "Recovered Rebates Count",
      stack: "values",
      color: "rgb(18 95 0)",
    },
  ]

  const seriesByRecoveredCount = [
    {
      dataKey: "Resolved Commission Count",
      label: "Recovered Commission Count",
      stack: "value",
    },
    {
      dataKey: "Resolved Rebates Count",
      label: "Recovered Rebates Count",
      stack: "value",
    },
  ]

  const seriesByEscalatedAmount = [
    {
      dataKey: "Escalated Commission",
      label: "Escalated Commission",
      stack: "value",
      valueFormatter: (value) => `$ ${value?.toLocaleString()}`,
    },
    {
      dataKey: "Escalated Rebates",
      label: "Escalated Rebates",
      stack: "value",
      valueFormatter: (value) => `$ ${value?.toLocaleString()}`,
    },

    {
      dataKey: "Resolved Commission",
      label: "Recovered Commission",
      stack: "values",
      color: "rgb(48 255 0)",
      valueFormatter: (value) => `$ ${value?.toLocaleString()}`,
    },
    {
      dataKey: "Resolved Rebates",
      label: "Recovered Rebates",
      stack: "values",
      color: "rgb(18 95 0)",
      valueFormatter: (value) => `$ ${value?.toLocaleString()}`,
    },
  ]

  const seriesByRecoveredAmount = [
    {
      dataKey: "Resolved Commission",
      label: "Recovered Commission",
      stack: "value",
      valueFormatter: (value) => `$ ${value?.toLocaleString()}`,
    },
    {
      dataKey: "Resolved Rebates",
      label: "Recovered Rebates",
      stack: "value",
      valueFormatter: (value) => `$ ${value?.toLocaleString()}`,
    },
  ]
  return (
    <Card
      sx={{
        padding: 2,
        // background: "#e4e4e463",
        // background: "var(--va-card-1)",
        position: "relative",
        // minWidth: 740,
        minHeight: 200,
        height: `${cardHeight}px`,
        transition: `${cardHeight == defaultHeight ? "0.4s all ease" : "0.4s all ease"}`,
        paddingBottom: "40px",
      }}
      ref={cardRef}
      className="visual-info-main-card"
      onDoubleClick={
        // handleExpand
        () => {
          if (!info?.rows) return
          // setCardHeight()
          const expandedHeight = info?.rows?.length * rowHeight + tableExtras
          const newHeight = !isExpanded ? expandedHeight : defaultHeight

          // console.log("newHeight", newHeight)
          // console.log("defaultHeight", defaultHeight)
          // console.log("isExpanded", isExpanded)

          setCardHeight((prevHeight) => {
            // const isExpanding = !isExpanded
            // const newHeight = isExpanded ? defaultHeight : expandedHeight

            // Scroll into view *after* state update

            // setTimeout(() => {
            //   if (isExpanding && cardRef.current) {
            //     cardRef.current.scrollIntoView({
            //       behavior: "smooth",
            //       block: "end",
            //     })
            //   }
            // }, 500)

            return newHeight
          })
          setIsExpanded(!isExpanded)

          if (!isExpanded) {
            setTimeout(() => {
              cardRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
            }, 400)
          }
        }
      }
    >
      {!hasDemoAccess && (
        <LoadingScreen
          h1={"Loading Sales Data..."}
          style={{
            position: "absolute",
            width: "100%",
            left: 0,
            top: 0,
            backgroundColor: "rgb(0,0,0,0.6)",
            color: "#fff",
            height: "100%",
            zIndex: 99,
            cursor: "default",
          }}
        />
      )}

      <DemoCTA open={showDemoCTA} onClose={() => setShowDemoCTA(false)} />

      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 20,
          }}
        >
          <div style={{ display: "flex", width: "100%", flexWrap: "wrap" }}>
            <div style={{ flex: 1 }}>
              <Typography
                variant="h6"
                gutterBottom
                style={{
                  color: "var(--va-primary-heading)",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                }}
              >
                Escalations By Stores
              </Typography>
            </div>
            <div
              style={{
                display: "flex",
                gap: "20px",
                height: "max-content",
                marginTop: "5px",
                flexWrap: "wrap",
              }}
            >
              <DatePicker
                label="Month"
                // views={["month"]}
                views={["month"]}
                value={selectedMonth}
                onChange={(newMonth) => setShowDemoCTA(true)}
                shouldDisableMonth={(date) => {
                  const month = date.month() + 1 // 0-based to 1-based
                  return !availableMonths.includes(month)
                }}
                disabled={!availableMonths.length}
                slotProps={{
                  actionBar: {
                    actions: ["clear", "accept"], // ✅ Adds built-in "Clear" button
                  },
                  textField: {
                    placeholder: "Select month",
                    variant: "outlined",
                    size: "small",
                    InputProps: {
                      disableUnderline: true,
                    },
                    InputLabelProps: {
                      shrink: true,
                    },
                    sx: {
                      background: "var(--va-primary-bg)",
                      width: 150,
                      border: "0px",

                      "& .MuiDateCalendar-root": {
                        paddingTop: "10px",
                      },

                      "& .MuiInputLabel-root": {
                        // fontSize: "0.75rem",
                        color: "var(--va-text-primary-color)",
                        fontWeight: "bold",
                        fontFamily: "Montserrat, san-serif",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                        color: "var(--va-text-primary-color)",
                      },

                      " & .MuiPickersSectionList-section span": {
                        color: "var(--va-text-primary-color)",
                      },
                      // "& .MuiPickersOutlinedInput-notchedOutline": {
                      //   border: "1px solid var(--va-text-primary-color)",
                      // },
                    },
                  },
                }}
                slots={{
                  actionBar: PickersActionBar, // ✅ built-in MUI component
                }}
              />

              <DatePicker
                label="Year"
                maxDate={currentDayjs}
                views={["year"]}
                value={selectedYear}
                yearsOrder="desc"
                onChange={handleYearChange}
                slotProps={{
                  textField: {
                    placeholder: "Select month",
                    variant: "outlined",
                    size: "small",
                    InputProps: {
                      disableUnderline: true,
                    },
                    InputLabelProps: {
                      shrink: true,
                    },
                    sx: {
                      background: "var(--va-primary-bg)",
                      width: 150,
                      "& .MuiInputLabel-root": {
                        // fontSize: "0.75rem",
                        color: "var(--va-text-primary-color)",
                        fontWeight: "bold",
                        fontFamily: "Montserrat, san-serif",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                        color: "var(--va-text-primary-color)",
                      },

                      " & .MuiPickersSectionList-section span": {
                        color: "var(--va-text-primary-color)",
                      },
                      // "& .MuiPickersOutlinedInput-notchedOutline": {
                      //   border: "1px solid var(--va-text-primary-color)",
                      // },
                    },
                  },
                }}
                sx={{
                  minWidth: 150,
                  color: "#cecece",
                }}
              />
            </div>
          </div>

          <div>
            <ToggleButtonGroup className="appeals-info-groupby-button-group" size="small" color="primary" value={view} exclusive onChange={handleViewChange} aria-label="Escalation View">
              {/* <ToggleButton value="by_recovered_count">By Recovered Count</ToggleButton> */}
              <ToggleButton value="by_escalated_amount">By Amount</ToggleButton>
              <ToggleButton value="by_escalated_count">By Count</ToggleButton>

              {/* <ToggleButton value="by_recovered_amount">By Recovered Amount</ToggleButton> */}
            </ToggleButtonGroup>
          </div>
        </div>

        <div style={{ maxWidth: "610px" }}>
          {info?.rows && (
            <BarChart
              dataset={info?.rows}
              grid={{ vertical: true }}
              layout="horizontal"
              yAxis={[
                {
                  dataKey: "store_label",
                  scaleType: "band",
                  label: `${info?.organizationName} Stores`,
                  tickLabelInterval: 0, // ensure all ticks are shown
                  labelStyle: {
                    fontSize: 14,
                    fontWeight: 600,
                    fill: "#000", // axis label color (optional)
                    fontFamily: "Roboto Mono",
                  },
                  tickLabelStyle: {
                    fill: "#000", // Optional: color
                    fontSize: 13,
                    fontWeight: 500,
                    dominantBaseline: "middle", // 👈 Important for vertical alignment
                  },
                  tickLabelAlign: "center", // 👈 Center align the label
                  tickLabelPosition: "middle", // 👈 Ensure it's placed in the middle of the tick band
                  width: 150, // Increase width for multiline labels
                },
              ]}
              xAxis={[
                {
                  label: `Total Escalations: ${info?.totalEscalations} `,
                  tickMinStep: 100,
                  // valueFormatter: (value) => `$ ${value.toLocaleString()}`,
                  tickLabelInterval: 0, // ensure all ticks are shown
                  tickLabelStyle: {
                    fill: "#fff", // Optional: color
                    fontSize: 13,
                    fontWeight: 500,
                    textAnchor: "middle",
                  },
                  labelStyle: {
                    fontSize: 12,
                    fontWeight: 600,
                    fill: "#000", // axis label color (optional)
                  },
                },
              ]}
              series={
                view === "by_escalated_count"
                  ? seriesByEscalatedCount
                  : view === "by_recovered_count"
                  ? seriesByRecoveredCount
                  : view === "by_escalated_amount"
                  ? seriesByEscalatedAmount
                  : seriesByRecoveredAmount
              }
              //   height: 300,
              {...chartSetting}
            />
          )}
        </div>
      </div>

      {cardHeight <= defaultHeight ? (
        <motion.div
          style={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            color: "#555",
            fontSize: 12,
            cursor: "pointer",
            pointerEvents: "none",
          }}
          animate={{ y: [0, -5, 0] }}
          transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
        >
          <DoubleArrowIcon fontSize="small" />
          <span>Double click to expand</span>
        </motion.div>
      ) : //   <motion.div
      //     style={{
      //       position: "absolute",
      //       bottom: 8,
      //       left: "20%",
      //       transform: "translateX(-50%)",
      //       display: "flex",
      //       flexDirection: "column",
      //       alignItems: "center",
      //       color: "#555",
      //       fontSize: 12,
      //       cursor: "pointer",
      //       pointerEvents: "none",
      //     }}
      //     animate={{ y: [0, -5, 0] }}
      //     transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
      //   >
      //     <DoubleArrowIcon fontSize="small" style={{ transform: "rotate(180deg)" }} />
      //     <span>Double click to contract</span>
      //   </motion.div>
      null}
    </Card>
  )
}

export default AppealsInfoGroupBy
